// Attendance API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// and cross-domain calls resolve through the shared HttpClient base.
// Courses and electives keep the same weekly class schedule, under different paths.
const classSchedulePath = (kind) => (kind === 'elective' ? '/attendance/elective-class-schedule' : '/attendance/class-schedule');

const withAttendanceApi = (Base) => class extends Base {
  // Attendance API methods
  async createAttendanceSession(sessionData) {
    // sessionData can have either courseId or electiveId
    return this.request('/attendance/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  async getStudentCalendarSessions() {
    return this.request('/attendance/student/calendar-sessions');
  }

  async getAttendanceSessions(courseId) {
    return this.request(`/attendance/sessions/course/${courseId}`);
  }

  async getElectiveAttendanceSessions(electiveId) {
    return this.request(`/attendance/sessions/elective/${electiveId}`);
  }

  // Scheduled attendance sessions (created automatically by a worker at the
  // scheduled time).
  async createScheduledAttendanceSession(payload) {
    return this.request('/attendance/scheduled-sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Upcoming class days for a course from its saved meeting days (+ the course's class times). */
  async getClassSchedule(id, { upcoming, kind, earlier } = {}) {
    const params = [upcoming ? `upcoming=${upcoming}` : '', earlier ? 'earlier=true' : ''].filter(Boolean);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.request(`${classSchedulePath(kind)}/${id}${query}`);
  }

  /** Every canceled class day for a course (past and upcoming), with who canceled it. */
  async getClassCancellations(id, kind) {
    return this.request(`${classSchedulePath(kind)}/${id}/cancellations`);
  }

  /** Automatic sessions for a course or elective: on/off, the next one, what failed, what was skipped. */
  async getAutoSessionStatus(kind, id) {
    return this.request(`/attendance/auto-sessions/${kind}/${id}`);
  }

  /** Clear a failed automatic session from view (its class is over, or it is not wanted). */
  async dismissAutoSession(kind, id, scheduledId) {
    return this.request(`/attendance/auto-sessions/${kind}/${id}/dismiss/${scheduledId}`, { method: 'POST' });
  }

  /** Plan an automatic session again after someone canceled it by hand. */
  async planAutoSessionAgain(kind, id, scheduledId) {
    return this.request(`/attendance/auto-sessions/${kind}/${id}/plan-again/${scheduledId}`, { method: 'POST' });
  }

  /** (Teacher) the next canceled classes of the courses and electives I manage; an admin gets all of them. */
  async getUpcomingCancellations() {
    return this.request('/attendance/upcoming-cancellations');
  }

  /** What canceling (or moving) a class would do to the week numbers, without saving anything. */
  async getClassCancelPreview(id, { date, movedTo, kind } = {}) {
    const params = [`date=${encodeURIComponent(date)}`, movedTo ? `movedTo=${encodeURIComponent(movedTo)}` : ''].filter(Boolean);
    return this.request(`${classSchedulePath(kind)}/${id}/cancel-preview?${params.join('&')}`);
  }

  /** (Admin) the courses that meet on a date, to cancel them together. */
  async getClassesOnDate(date) {
    return this.request(`/attendance/class-schedule-day?date=${encodeURIComponent(date)}`);
  }

  /** (Admin) cancel several courses' class on one date ({ date, reason, courseIds }). */
  async cancelClassDay({ date, reason, courseIds, electiveIds }) {
    return this.request('/attendance/cancel-day', {
      method: 'POST',
      body: JSON.stringify({ date, reason: reason || null, courseIds, electiveIds }),
    });
  }

  /** Cancel one upcoming class day ({ date: 'YYYY-MM-DD', reason? }). */
  async cancelClass(id, { date, reason, movedTo }, kind) {
    return this.request(`${classSchedulePath(kind)}/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ date, reason: reason || null, movedTo: movedTo || null }),
    });
  }

  /** Put a canceled class day back. */
  async restoreClass(id, date, kind) {
    return this.request(`${classSchedulePath(kind)}/${id}/cancel/${date}`, { method: 'DELETE' });
  }

  async getScheduledAttendanceSessions({ courseId, electiveId } = {}) {
    const params = new URLSearchParams();
    if (courseId) params.set('courseId', String(courseId));
    if (electiveId) params.set('electiveId', String(electiveId));
    return this.request(`/attendance/scheduled-sessions?${params.toString()}`);
  }

  async cancelScheduledAttendanceSession(id) {
    return this.request(`/attendance/scheduled-sessions/${id}`, {
      method: 'DELETE',
    });
  }

  async retryScheduledAttendanceSession(id) {
    return this.request(`/attendance/scheduled-sessions/${id}/retry`, {
      method: 'POST',
    });
  }

  async getAttendanceSession(sessionId) {
    return this.request(`/attendance/sessions/${sessionId}`);
  }

  async updateAttendanceRecords(sessionId, records) {
    return this.request(`/attendance/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify({ records })
    });
  }

  async deleteAttendanceSession(sessionId) {
    return this.request(`/attendance/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }

  async getStudentAttendance(courseId) {
    return this.request(`/attendance/student/${courseId}`);
  }

  async getStudentElectiveAttendance(electiveId) {
    return this.request(`/attendance/student/elective/${electiveId}`);
  }

  async getActiveAttendanceSessions() {
    return this.request('/attendance/student/active-sessions');
  }

  async checkInAttendance(sessionId, status = 'present') {
    return this.request(`/attendance/checkin/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({ status }),
      retryOnNetwork: true
    });
  }

  async updateAttendanceSession(sessionId, sessionData) {
    return this.request(`/attendance/sessions/${sessionId}/details?v=${Date.now()}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData)
    });
  }

  // Attendance correction requests
  async requestAttendanceCorrection(sessionId, requestedStatus, reason) {
    return this.request('/attendance/correction-requests', {
      method: 'POST',
      body: JSON.stringify({ sessionId, requestedStatus, reason })
    });
  }

  async getMyAttendanceCorrections() {
    return this.request('/attendance/correction-requests/mine');
  }

  async getAttendanceCorrectionRequests(status = 'pending') {
    return this.request(`/attendance/correction-requests?status=${encodeURIComponent(status)}`);
  }

  async reviewAttendanceCorrection(requestId, action, reviewNotes) {
    return this.request(`/attendance/correction-requests/${requestId}/review`, {
      method: 'PUT',
      body: JSON.stringify({ action, reviewNotes })
    });
  }

  async getCorrectionSettings({ courseId, electiveId } = {}) {
    const params = new URLSearchParams();
    if (courseId) params.set('courseId', courseId);
    if (electiveId) params.set('electiveId', electiveId);
    const qs = params.toString();
    return this.request(`/attendance/correction-settings${qs ? `?${qs}` : ''}`);
  }

  async updateCorrectionSettings({ scope, scopeId, limit }) {
    return this.request('/attendance/correction-settings', {
      method: 'PUT',
      body: JSON.stringify({ scope, scopeId, limit })
    });
  }

  // Course Content API methods
  async getCourseRecordings(courseId) {
    return this.request(`/course-content/recordings/${courseId}`);
  }

  async createCourseRecording(recordingData) {
    return this.request('/course-content/recordings', {
      method: 'POST',
      body: JSON.stringify(recordingData)
    });
  }

  async getCourseAnnouncements(courseId) {
    return this.request(`/course-content/announcements/${courseId}`);
  }

  async createCourseAnnouncement(announcementData) {
    return this.request('/course-content/announcements', {
      method: 'POST',
      body: JSON.stringify(announcementData)
    });
  }

  async getCourseSyllabus(courseId) {
    return this.request(`/course-content/syllabus/${courseId}`);
  }

  async createCourseSyllabus(syllabusData) {
    return this.request('/course-content/syllabus', {
      method: 'POST',
      body: JSON.stringify(syllabusData)
    });
  }

  async getCourseSlides(courseId) {
    return this.request(`/course-content/slides/${courseId}`);
  }

  async createCourseSlide(slideData) {
    return this.request('/course-content/slides', {
      method: 'POST',
      body: JSON.stringify(slideData)
    });
  }

  // CSV Import methods
  async importCSV(file) {
    const formData = new FormData();
    formData.append('csvFile', file);

    const response = await fetch(`${this.baseURL}/csv-import/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Import failed');
    }

    return await response.json();
  }

  async confirmCSVImport(filePath, totalRecords) {
    const response = await fetch(`${this.baseURL}/csv-import/confirm-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      },
      body: JSON.stringify({ filePath, totalRecords })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Import confirmation failed');
    }

    return await response.json();
  }

  async downloadCSVTemplate() {
    const response = await fetch(`${this.baseURL}/csv-import/template`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

};

export default withAttendanceApi;
